import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import {
  GRID_COLS,
  THEME_COLORS,
  getUnlockedMonsterIds,
  getHiddenMonsterDisplayMode,
  getCharacterSelectButtons,
  getChallengeHeader,
  CHARACTER_SELECT_HEADERS,
  CharacterSelectStep,
  DETAIL_PANEL_CONFIG,
} from './characterSelectConfig';
import { MONSTER_DATABASE, HIDDEN_MONSTER_IDS, getMonsterDetailData } from '../constants/monsters';
import { Monster } from '../types/Monster';
import { GameMode } from '../types/GameMode';
import { loadGameProgress } from '../utils/gameProgressManager';
import { getMonsterPortraitKey, UIImageKey, BackgroundImageKey } from '../constants/imageKeys';
import { getNextStageNumber } from '../constants/challengeConfig';
import { EREBOS_MONSTER, EREBOS_FORM2_MONSTER, BOSS_STAGE_NUMBER } from '../constants/bossConfig';
import { PartyMemberState } from '../battle/partyBattle';
import { getMonsterWithGrownStats, getMonsterWithFinalStats, MAX_GROWTH_STAGES } from '../constants/monsterStats';
import { SocketClient } from '../network/SocketClient';
import { BattleStartedPayload } from '../../shared/types/SocketEvents';
import { deleteSurvivalSave, loadSurvivalBest } from '../utils/survivalManager';
import { loadCustomAICharacters } from '../utils/customAIManager';
import { CustomAICharacter } from '../types/CustomAICharacter';
import { AIConfig } from '../ai/types';
import {
  createShuffledOpponentQueue,
  getSurvivalAiLevel,
  getSurvivalEnemyStage,
} from '../constants/survivalConfig';
import { SurvivalSaveData } from '../types/SurvivalSaveData';
import { AudioKey, AudioKeyValue } from '../constants/audioKeys';
import { playBgm, playSe } from '../utils/audioManager';
import { logCharacterSelect, logModeSelect } from '../utils/firebaseAnalytics';

/** CharacterSelectSceneに渡されるデータ */
export interface CharacterSelectSceneData {
  mode?: GameMode;
  step?: CharacterSelectStep;
  playerMonsterId?: string;
  cpu1MonsterId?: string;
  clearedStages?: number;
  stageNumber?: number;
  socketClient?: SocketClient;
  roomId?: string;
  playerNumber?: 1 | 2;
  // カスタムAI用
  playerAIConfig?: AIConfig;
  cpu1AIConfig?: AIConfig;
  // 3vs3用
  playerParty?: string[];
  enemyParty?: string[];
  // BGM自由選択
  selectedBgmKey?: string;
  // バトルマップ選択
  selectedBattleBg?: string;
  // キャンペーン再プレイフラグ
  isReplay?: boolean;
  // ボスステージフラグ
  isBossStage?: boolean;
}

/**
 * キャラ選択画面シーン
 *
 * 8魂格の選択グリッド、ロック表示、パラメータ表示、決定/戻るボタンを表示する。
 * FREE_CPUモードでは player/opponent の2ステップに対応。
 */
export class CharacterSelectScene extends BaseScene {
  private selectedMonsterId: string | null = null;
  private hoveredMonsterId: string | null = null;
  private cellBgs: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private unlockedIds: string[] = [];
  private parameterTexts: Phaser.GameObjects.Text[] = [];
  private gridCells: Phaser.GameObjects.Container[] = [];
  private detailPanelObjects: Phaser.GameObjects.GameObject[] = [];
  private mode?: GameMode;
  private step?: CharacterSelectStep;
  private playerMonsterId?: string;
  private cpu1MonsterId?: string;
  private stageNumber?: number;
  private clearedStages?: number;
  private isReplay?: boolean;
  private socketClient?: SocketClient;
  private roomId?: string;
  private playerNumber?: 1 | 2;
  private waitingText?: Phaser.GameObjects.Text;
  // カスタムAI用
  private selectedCustomAIConfig?: AIConfig;
  private playerAIConfig?: AIConfig;
  private cpu1AIConfig?: AIConfig;
  // 3vs3用
  private playerParty: string[] = [];
  private enemyParty: string[] = [];
  private customAICardBgs: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  // BGM自由選択
  private selectedBgmKey: AudioKeyValue | 'bgm_random' = 'bgm_random';
  // バトルマップ選択
  private selectedBattleBg: string = 'bg_random';
  // ボスステージ
  private isBossStage = false;

  constructor() {
    super(SceneKey.CHARACTER_SELECT);
  }

  create(data?: CharacterSelectSceneData): void {
    playBgm(this.sound, AudioKey.BGM_CHARACTER_SELECT);
    if (this.textures.exists(BackgroundImageKey.CHARACTER_SELECT)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.CHARACTER_SELECT).setOrigin(0.5);
    }
    this.mode = data?.mode;
    // FREE_CPUでstepが未指定の場合はプレイヤー選択ステップとして扱う
    // SPECTATORでstepが未指定の場合はcpu1選択ステップとして扱う
    // CHALLENGEボスステージでstepが未指定の場合はplayerステップとして扱う
    if (this.mode === GameMode.FREE_CPU) {
      this.step = data?.step ?? 'player';
    } else if (this.mode === GameMode.FREE_CPU_3V3) {
      this.step = data?.step ?? 'player';
    } else if (this.mode === GameMode.SPECTATOR) {
      this.step = data?.step ?? 'cpu1';
    } else if (this.mode === GameMode.CHALLENGE && data?.isBossStage) {
      this.step = data?.step ?? 'player';
    } else {
      this.step = data?.step;
    }
    this.playerMonsterId = data?.playerMonsterId;
    this.cpu1MonsterId = data?.cpu1MonsterId;
    this.playerParty = data?.playerParty ?? [];
    this.enemyParty = data?.enemyParty ?? [];
    this.socketClient = data?.socketClient;
    this.roomId = data?.roomId;
    this.playerNumber = data?.playerNumber;
    this.playerAIConfig = data?.playerAIConfig;
    this.cpu1AIConfig = data?.cpu1AIConfig;
    this.selectedCustomAIConfig = undefined;
    this.customAICardBgs = new Map();
    this.selectedBgmKey = (data?.selectedBgmKey as AudioKeyValue | 'bgm_random' | undefined) ?? 'bgm_random';
    this.selectedBattleBg = data?.selectedBattleBg ?? 'bg_random';

    // FREE_LOCAL: キャラ選択中（確定前）に相手が戻った場合の処理
    if (this.mode === GameMode.FREE_LOCAL && this.socketClient) {
      this.socketClient.updateCallbacks({
        onOpponentLeft: () => {
          this.socketClient?.leaveRoom();
          this.transitionTo(SceneKey.BATTLE_MODE_SELECT);
        },
      });
    }

    // CHALLENGEモード：進捗からステージ番号を決定（dataに値があればそちらを優先）
    if (this.mode === GameMode.CHALLENGE) {
      const progress = loadGameProgress();
      this.clearedStages = data?.clearedStages ?? progress.clearedStages;
      this.stageNumber = data?.stageNumber ?? getNextStageNumber(progress.clearedStages) ?? undefined;
      this.isReplay = data?.isReplay;
      this.isBossStage = data?.isBossStage ?? (this.stageNumber === BOSS_STAGE_NUMBER);
    }

    // FREE_CPU/FREE_CPU_3V3/SPECTATOR/SURVIVALは全キャラ解放、CHALLENGEは進捗ベース
    const clearedStages =
      this.mode === GameMode.FREE_CPU ||
      this.mode === GameMode.FREE_CPU_3V3 ||
      this.mode === GameMode.SPECTATOR ||
      this.mode === GameMode.SURVIVAL
        ? 7
        : this.mode === GameMode.CHALLENGE
          ? (this.clearedStages ?? 0)
          : (data?.clearedStages ?? 7);
    const unlockedIds = getUnlockedMonsterIds(clearedStages);
    this.unlockedIds = unlockedIds;

    this.createHeader();
    this.createGrid(unlockedIds);
    this.createParameterPanel();

    // FREE_CPU(player選択) / FREE_CPU_3V3(自分側全ステップ) / SPECTATOR(cpu1/cpu2選択) のみカスタムAIセクション表示
    const showCustomAI =
      (this.mode === GameMode.FREE_CPU && this.step === 'player') ||
      (this.mode === GameMode.FREE_CPU_3V3 && (this.step === 'player' || this.step === 'player2' || this.step === 'player3')) ||
      (this.mode === GameMode.SPECTATOR);
    if (showCustomAI) {
      this.createCustomAISection();
    }

    this.createButtons();
    this.createDetailButton();

    // BGM・マップ選択UI（FREE_CPU/FREE_CPU_3V3/FREE_LOCAL のplayer選択ステップ、および SPECTATOR の cpu1 選択ステップ）
    const showBgmSelect =
      (this.mode === GameMode.FREE_CPU && this.step === 'player') ||
      (this.mode === GameMode.FREE_CPU_3V3 && this.step === 'player') ||
      this.mode === GameMode.FREE_LOCAL ||
      (this.mode === GameMode.SPECTATOR && this.step === 'cpu1');
    if (showBgmSelect) {
      this.createBgmSelector();
      this.createBattleBgSelector();
    }

    // 初期選択（最初の解放済みキャラ）
    this.selectMonster(unlockedIds[0]);
  }

  private createHeader(): void {
    let headerText: string;
    if (this.mode === GameMode.CHALLENGE && this.stageNumber) {
      const replayPrefix = this.isReplay ? '【再挑戦】' : '';
      const stageHeader = `${replayPrefix}${getChallengeHeader(this.stageNumber)}`;
      if (this.isBossStage) {
        const stepNum = this.step === 'player' ? 1 : this.step === 'player2' ? 2 : 3;
        headerText = `${stageHeader}　（${stepNum}/3体目を選択）`;
      } else {
        headerText = stageHeader;
      }
    } else if (this.step === 'player') {
      headerText = CHARACTER_SELECT_HEADERS.player;
    } else if (this.step === 'player2') {
      headerText = CHARACTER_SELECT_HEADERS.player2;
    } else if (this.step === 'player3') {
      headerText = CHARACTER_SELECT_HEADERS.player3;
    } else if (this.step === 'opponent') {
      headerText = CHARACTER_SELECT_HEADERS.opponent;
    } else if (this.step === 'opponent2') {
      headerText = CHARACTER_SELECT_HEADERS.opponent2;
    } else if (this.step === 'opponent3') {
      headerText = CHARACTER_SELECT_HEADERS.opponent3;
    } else if (this.step === 'cpu1') {
      headerText = CHARACTER_SELECT_HEADERS.cpu1;
    } else if (this.step === 'cpu2') {
      headerText = CHARACTER_SELECT_HEADERS.cpu2;
    } else {
      headerText = CHARACTER_SELECT_HEADERS.default;
    }

    const isPlayerSide = this.step === 'player' || this.step === 'player2' || this.step === 'player3';
    const isOpponentSide = this.step === 'opponent' || this.step === 'opponent2' || this.step === 'opponent3'
      || this.step === 'cpu1' || this.step === 'cpu2';
    const headerColor = isPlayerSide ? '#66aaff' : isOpponentSide ? '#ff6666' : '#ffffff';

    this.add
      .text(GAME_WIDTH / 2, 30, headerText, {
        fontSize: '32px',
        color: headerColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    if (this.isBossStage) {
      this.createBossPartySlots();
    }
  }

  private createBossPartySlots(): void {
    const slots = 3;
    const slotW = 180;
    const slotH = 28;
    const gap = 12;
    const totalW = slots * slotW + (slots - 1) * gap;
    const startX = (GAME_WIDTH - totalW) / 2;
    const y = 62;

    const getName = (id: string) => MONSTER_DATABASE.find((m) => m.id === id)?.name ?? id;

    for (let i = 0; i < slots; i++) {
      const cx = startX + i * (slotW + gap) + slotW / 2;
      const filled = i < this.playerParty.length;
      this.add.rectangle(cx, y, slotW, slotH, filled ? 0x336633 : 0x333355, 1)
        .setStrokeStyle(1, filled ? 0x88ff88 : 0x666699);
      const label = filled ? getName(this.playerParty[i]) : `${i + 1}人目`;
      this.add.text(cx, y, label, {
        fontSize: '15px',
        color: filled ? '#aaffaa' : '#888899',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      }).setOrigin(0.5);
    }
  }

  private createGrid(unlockedIds: string[]): void {
    const gridStartX = 50;
    const gridStartY = 80;
    const cellWidth = (GAME_WIDTH - 100 - 250) / GRID_COLS;
    const cellHeight = 80;

    const progress = loadGameProgress();

    // 表示対象モンスターを決定
    // CHALLENGEモードでは隠しキャラは使用不可なので一切表示しない
    const visibleMonsters = MONSTER_DATABASE.filter((monster) => {
      const mode = getHiddenMonsterDisplayMode(monster.id, progress);
      if (mode === 'none') return false;
      if (this.mode === GameMode.CHALLENGE && HIDDEN_MONSTER_IDS.includes(monster.id)) return false;
      return true;
    });

    visibleMonsters.forEach((monster, index) => {
      const col = index % GRID_COLS;
      const row = Math.floor(index / GRID_COLS);
      const x = gridStartX + col * cellWidth + cellWidth / 2;
      const y = gridStartY + row * cellHeight + cellHeight / 2;
      const isUnlocked = unlockedIds.includes(monster.id);
      const hiddenMode = getHiddenMonsterDisplayMode(monster.id, progress);

      const container = this.add.container(x, y);

      if (hiddenMode === 'unknown') {
        // ？？？表示（ボス撃破済み・未解放の隠しキャラ）
        const bg = this.add
          .rectangle(0, 0, cellWidth - 10, cellHeight - 10, 0x1a1a2e)
          .setStrokeStyle(2, 0x4a4a6a);
        container.add(bg);
        const questionText = this.add
          .text(0, -5, '？？？', {
            fontSize: '20px',
            color: '#6666aa',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
            fontStyle: 'bold',
          })
          .setOrigin(0.5);
        container.add(questionText);
        const hintText = this.add
          .text(0, 18, '???', {
            fontSize: '11px',
            color: '#444466',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
          })
          .setOrigin(0.5);
        container.add(hintText);
      } else if (isUnlocked || (hiddenMode === 'available' && (
        this.mode === GameMode.FREE_CPU ||
        this.mode === GameMode.FREE_CPU_3V3 ||
        this.mode === GameMode.SPECTATOR ||
        this.mode === GameMode.SURVIVAL
      ))) {
        // 通常表示（解放済み、または対戦/サバイバル系モードで解放済みの隠しキャラ）
        const bg = this.add
          .rectangle(0, 0, cellWidth - 10, cellHeight - 10, 0x333355)
          .setStrokeStyle(2, 0x666699);
        container.add(bg);

        // ポートレート画像
        const portraitKey = getMonsterPortraitKey(monster.id);
        if (this.textures.exists(portraitKey)) {
          const portrait = this.add.image(0, 0, portraitKey).setOrigin(0.5).setDisplaySize(60, 60);
          container.add(portrait);
        }

        // ボス撃破済みマーク（キャンペーンモードのみ）
        if (this.mode === GameMode.CHALLENGE) {
          if ((progress.bossDefeatedWith ?? []).includes(monster.id)) {
            const crown = this.add.text(
              (cellWidth - 10) / 2 - 4,
              -(cellHeight - 10) / 2 + 4,
              '👑',
              { fontSize: '14px' }
            ).setOrigin(1, 0);
            container.add(crown);
          }
        }

        // インタラクティブ設定
        this.cellBgs.set(monster.id, bg);
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => { playSe(this.sound, AudioKey.SE_SELECT); this.selectMonster(monster.id); });
        bg.on('pointerover', () => {
          this.hoveredMonsterId = monster.id;
          this.applyCellColor(monster.id);
        });
        bg.on('pointerout', () => {
          if (this.hoveredMonsterId === monster.id) this.hoveredMonsterId = null;
          this.applyCellColor(monster.id);
        });
      } else {
        // ロック表示（通常キャラ・未解放）
        const bg = this.add
          .rectangle(0, 0, cellWidth - 10, cellHeight - 10, 0x1a1a1a)
          .setStrokeStyle(2, 0x333333);
        container.add(bg);

        if (this.textures.exists(UIImageKey.LOCK_ICON)) {
          const lockIcon = this.add.image(0, -8, UIImageKey.LOCK_ICON).setOrigin(0.5).setDisplaySize(36, 36);
          container.add(lockIcon);
        } else {
          const lockText = this.add
            .text(0, -8, '🔒', { fontSize: '22px' })
            .setOrigin(0.5);
          container.add(lockText);
        }

        const lockedLabel = this.add
          .text(0, 16, '未解放', {
            fontSize: '12px',
            color: '#555555',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
          })
          .setOrigin(0.5);
        container.add(lockedLabel);
      }

      this.gridCells.push(container);
    });
  }

  private createParameterPanel(): void {
    const panelX = GAME_WIDTH - 220;
    const panelY = 80;
    // サバイバルモードは最高連勝数行が追加されるため高さを拡張
    const panelHeight = this.mode === GameMode.SURVIVAL ? 310 : 290;
    const panelCenterOffsetY = this.mode === GameMode.SURVIVAL ? 140 : 130;

    this.add
      .rectangle(panelX + 100, panelY + panelCenterOffsetY, 220, panelHeight, 0x222244)
      .setStrokeStyle(2, 0x444488);

    this.add
      .text(panelX + 100, panelY + 5, 'パラメータ', {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private createButtons(): void {
    const buttons = getCharacterSelectButtons(this.step, this.mode);
    const buttonY = GAME_HEIGHT - 50;
    const buttonSpacing = buttons.length > 2 ? 150 : 200;

    buttons.forEach((buttonConfig, index) => {
      const centerOffset = (buttons.length - 1) / 2;
      const x = GAME_WIDTH / 2 + (index - centerOffset) * buttonSpacing;

      const color = buttonConfig.action === 'confirm' ? '#88ff88'
        : buttonConfig.action === 'random' ? '#ffcc44'
        : '#ffffff';

      const text = this.add
        .text(x, buttonY, buttonConfig.label, {
          fontSize: '24px',
          color,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => text.setScale(1.1));
      text.on('pointerout', () => text.setScale(1.0));

      text.on('pointerdown', () => {
        playSe(this.sound, AudioKey.SE_SELECT);
        this.handleButtonClick(buttonConfig);
      });
    });
  }

  private handleButtonClick(buttonConfig: { action: string; targetScene: SceneKey }): void {
    if (buttonConfig.action === 'confirm' && this.selectedMonsterId) {
      logModeSelect(this.mode ?? 'unknown');
      logCharacterSelect(this.selectedMonsterId);
      if (this.mode === GameMode.SURVIVAL) {
        // SURVIVAL → EXP割り振り画面（セーブデータ生成して最初のバトルへ）
        this.startSurvivalMode();
        return;
      }
      if (this.mode === GameMode.CHALLENGE) {
        if (this.isBossStage) {
          // ボスステージ: 3体選択フロー
          this.handleBossStageConfirm(this.selectedMonsterId);
          return;
        }
        // 通常CHALLENGE → BATTLE with stage info
        this.transitionTo(buttonConfig.targetScene, {
          monsterId: this.selectedMonsterId,
          mode: GameMode.CHALLENGE,
          stageNumber: this.stageNumber,
          clearedStages: this.clearedStages,
          isReplay: this.isReplay,
        });
        return;
      }
      if (this.mode === GameMode.FREE_LOCAL) {
        // FREE_LOCAL: startBattle を送信し、onBattleStarted を待つ（二重送信防止）
        if (!this.socketClient || !this.roomId || this.waitingText) return;
        this.showWaitingForOpponent();
        this.socketClient.updateCallbacks({
          onBattleStarted: (payload: BattleStartedPayload) => {
            const playerMonster = this.playerNumber === 1 ? payload.player1Monster : payload.player2Monster;
            const enemyMonster = this.playerNumber === 1 ? payload.player2Monster : payload.player1Monster;
            this.transitionTo(SceneKey.BATTLE, {
              mode: GameMode.FREE_LOCAL,
              isNetworkMode: true,
              roomId: this.roomId,
              socketClient: this.socketClient,
              playerNumber: this.playerNumber,
              playerMonster,
              enemyMonster,
              initialBattleState: payload.initialState,
              selectedBgmKey: this.selectedBgmKey,
              selectedBattleBg: this.selectedBattleBg,
            });
          },
          onDisconnect: () => this.transitionTo(SceneKey.MODE_SELECT),
          onOpponentLeft: () => {
            this.socketClient?.leaveRoom();
            this.transitionTo(SceneKey.MODE_SELECT);
          },
        });
        this.socketClient.startBattle(this.roomId, this.selectedMonsterId!);
        return;
      }
      if (this.mode === GameMode.SPECTATOR && this.step === 'cpu1') {
        // cpu1 → cpu2
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          step: 'cpu2',
          cpu1MonsterId: this.selectedMonsterId,
          cpu1AIConfig: this.selectedCustomAIConfig,
          selectedBgmKey: this.selectedBgmKey,
          selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.SPECTATOR && this.step === 'cpu2') {
        // cpu2 → difficulty (cpu1)
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          cpu1MonsterId: this.cpu1MonsterId,
          cpu2MonsterId: this.selectedMonsterId,
          cpu1AIConfig: this.cpu1AIConfig,
          cpu2AIConfig: this.selectedCustomAIConfig,
          selectedBgmKey: this.selectedBgmKey,
          selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.FREE_CPU && this.step === 'player') {
        // player → opponent
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          step: 'opponent',
          playerMonsterId: this.selectedMonsterId,
          playerAIConfig: this.selectedCustomAIConfig,
          selectedBgmKey: this.selectedBgmKey,
              selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.FREE_CPU && this.step === 'opponent') {
        // opponent → difficulty
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          playerMonsterId: this.playerMonsterId,
          enemyMonsterId: this.selectedMonsterId,
          playerAIConfig: this.playerAIConfig,
          selectedBgmKey: this.selectedBgmKey,
              selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.FREE_CPU_3V3) {
        this.handle3v3Confirm(this.selectedMonsterId!, buttonConfig.targetScene);
      } else {
        // デフォルト（既存動作）
        this.transitionTo(buttonConfig.targetScene, {
          monsterId: this.selectedMonsterId,
        });
      }
    } else if (buttonConfig.action === 'random') {
      if (this.mode === GameMode.SPECTATOR && this.step === 'cpu1') {
        // cpu1ランダム → cpu2選択
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          step: 'cpu2',
          cpu1MonsterId: this.getRandomMonsterId(),
          cpu1AIConfig: this.selectedCustomAIConfig,
          selectedBgmKey: this.selectedBgmKey,
          selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.SPECTATOR && this.step === 'cpu2') {
        // cpu2ランダム → difficulty with null cpu2
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          cpu1MonsterId: this.cpu1MonsterId,
          cpu2MonsterId: null,
          cpu1AIConfig: this.cpu1AIConfig,
          cpu2AIConfig: this.selectedCustomAIConfig,
          selectedBgmKey: this.selectedBgmKey,
          selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.FREE_CPU && this.step === 'player') {
        // playerランダム → opponent選択
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          step: 'opponent',
          playerMonsterId: this.getRandomMonsterId(),
          selectedBgmKey: this.selectedBgmKey,
              selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.FREE_CPU && this.step === 'opponent') {
        // opponentランダム → difficulty with null enemy
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          playerMonsterId: this.playerMonsterId,
          enemyMonsterId: null,
          playerAIConfig: this.playerAIConfig,
          selectedBgmKey: this.selectedBgmKey,
              selectedBattleBg: this.selectedBattleBg,
        });
      } else if (this.mode === GameMode.FREE_CPU_3V3) {
        this.handle3v3Confirm(this.getRandomMonsterId(), buttonConfig.targetScene);
      } else if (this.mode === GameMode.CHALLENGE) {
        if (this.isBossStage) {
          // ボスステージのランダム → 通常の決定と同じパーティ選択ロジックを使う
          this.handleBossStageConfirm(this.getRandomMonsterId());
        } else {
          // 通常CHALLENGEランダム → BATTLE with random player
          this.transitionTo(buttonConfig.targetScene, {
            monsterId: this.getRandomMonsterId(),
            mode: GameMode.CHALLENGE,
            stageNumber: this.stageNumber,
            clearedStages: this.clearedStages,
            isReplay: this.isReplay,
          });
        }
      } else if (this.mode === GameMode.FREE_LOCAL) {
        // FREE_LOCALランダム: startBattle with random monster
        if (!this.socketClient || !this.roomId || this.waitingText) return;
        this.showWaitingForOpponent();
        const randomId = this.getRandomMonsterId();
        this.socketClient.updateCallbacks({
          onBattleStarted: (payload: BattleStartedPayload) => {
            const playerMonster = this.playerNumber === 1 ? payload.player1Monster : payload.player2Monster;
            const enemyMonster = this.playerNumber === 1 ? payload.player2Monster : payload.player1Monster;
            this.transitionTo(SceneKey.BATTLE, {
              mode: GameMode.FREE_LOCAL,
              isNetworkMode: true,
              roomId: this.roomId,
              socketClient: this.socketClient,
              playerNumber: this.playerNumber,
              playerMonster,
              enemyMonster,
              initialBattleState: payload.initialState,
              selectedBgmKey: this.selectedBgmKey,
              selectedBattleBg: this.selectedBattleBg,
            });
          },
          onDisconnect: () => this.transitionTo(SceneKey.MODE_SELECT),
          onOpponentLeft: () => {
            this.socketClient?.leaveRoom();
            this.transitionTo(SceneKey.MODE_SELECT);
          },
        });
        this.socketClient.startBattle(this.roomId, randomId);
      } else {
        // デフォルト: ランダム → BATTLE with random player
        this.transitionTo(buttonConfig.targetScene, {
          monsterId: this.getRandomMonsterId(),
        });
      }
    } else if (buttonConfig.action === 'back') {
      if (this.mode === GameMode.FREE_LOCAL) {
        this.socketClient?.leaveRoom();
        this.transitionTo(buttonConfig.targetScene);
      } else if (this.mode === GameMode.FREE_CPU && this.step === 'opponent') {
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          step: 'player',
        });
      } else if (this.mode === GameMode.FREE_CPU_3V3) {
        this.handle3v3Back();
      } else if (this.mode === GameMode.SPECTATOR && this.step === 'cpu2') {
        this.transitionTo(buttonConfig.targetScene, {
          mode: this.mode,
          step: 'cpu1',
          selectedBgmKey: this.selectedBgmKey,
          selectedBattleBg: this.selectedBattleBg,
        });
      } else {
        this.transitionTo(buttonConfig.targetScene);
      }
    }
  }

  private getRandomMonsterId(): string {
    return this.unlockedIds[Math.floor(Math.random() * this.unlockedIds.length)];
  }

  /** ボスステージの「決定」処理（3体選択→BattleScene） */
  private handleBossStageConfirm(monsterId: string): void {
    if (this.playerParty.includes(monsterId)) return;
    const step = this.step;
    if (step === 'player') {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: GameMode.CHALLENGE,
        step: 'player2',
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        isBossStage: true,
        playerParty: [monsterId],
      });
    } else if (step === 'player2') {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: GameMode.CHALLENGE,
        step: 'player3',
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        isBossStage: true,
        playerParty: [...this.playerParty, monsterId],
      });
    } else if (step === 'player3') {
      const playerParty3 = [...this.playerParty, monsterId];
      const growthStages = Math.min(this.clearedStages ?? 0, 7);
      const playerPartyState: PartyMemberState[] = playerParty3.map((id) => ({
        monsterId: id,
        isDefeated: false,
        remainingSpecialCount: null,
        usedReflectCount: 0,
        currentHp: null,
      }));
      const enemyPartyState: PartyMemberState[] = [
        { monsterId: EREBOS_MONSTER.id, isDefeated: false, remainingSpecialCount: null, usedReflectCount: 0, currentHp: null },
        { monsterId: EREBOS_FORM2_MONSTER.id, isDefeated: false, remainingSpecialCount: null, usedReflectCount: 0, currentHp: null },
      ];
      this.transitionTo(SceneKey.BATTLE, {
        mode: GameMode.CHALLENGE,
        stageNumber: this.stageNumber,
        clearedStages: this.clearedStages,
        isBossStage: true,
        playerParty: playerPartyState,
        enemyParty: enemyPartyState,
        playerPartyIndex: 0,
        enemyPartyIndex: 0,
      });
      void growthStages;
    }
  }

  /** 3vs3モードの「決定」処理 */
  private handle3v3Confirm(monsterId: string, targetScene: SceneKey): void {
    const step = this.step;
    if (step === 'player') {
      this.transitionTo(targetScene, {
        mode: this.mode,
        step: 'player2',
        playerParty: [monsterId],
      });
    } else if (step === 'player2') {
      this.transitionTo(targetScene, {
        mode: this.mode,
        step: 'player3',
        playerParty: [...this.playerParty, monsterId],
      });
    } else if (step === 'player3') {
      this.transitionTo(targetScene, {
        mode: this.mode,
        step: 'opponent',
        playerParty: [...this.playerParty, monsterId],
        enemyParty: [],
      });
    } else if (step === 'opponent') {
      this.transitionTo(targetScene, {
        mode: this.mode,
        step: 'opponent2',
        playerParty: this.playerParty,
        enemyParty: [monsterId],
      });
    } else if (step === 'opponent2') {
      this.transitionTo(targetScene, {
        mode: this.mode,
        step: 'opponent3',
        playerParty: this.playerParty,
        enemyParty: [...this.enemyParty, monsterId],
      });
    } else if (step === 'opponent3') {
      // 全員選択完了 → 難易度選択
      this.transitionTo(targetScene, {
        mode: this.mode,
        playerParty: this.playerParty,
        enemyParty: [...this.enemyParty, monsterId],
        selectedBgmKey: this.selectedBgmKey,
              selectedBattleBg: this.selectedBattleBg,
      });
    }
  }

  /** 3vs3モードの「戻る」処理 */
  private handle3v3Back(): void {
    const step = this.step;
    if (step === 'player') {
      this.transitionTo(SceneKey.BATTLE_MODE_SELECT);
    } else if (step === 'player2') {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: this.mode,
        step: 'player',
      });
    } else if (step === 'player3') {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: this.mode,
        step: 'player2',
        playerParty: this.playerParty.slice(0, 1),
      });
    } else if (step === 'opponent') {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: this.mode,
        step: 'player3',
        playerParty: this.playerParty.slice(0, 2),
      });
    } else if (step === 'opponent2') {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: this.mode,
        step: 'opponent',
        playerParty: this.playerParty,
        enemyParty: [],
      });
    } else if (step === 'opponent3') {
      this.transitionTo(SceneKey.CHARACTER_SELECT, {
        mode: this.mode,
        step: 'opponent2',
        playerParty: this.playerParty,
        enemyParty: this.enemyParty.slice(0, 1),
      });
    }
  }

  private createCustomAISection(): void {
    const sectionY = 310;
    const gridLeft = 50;
    const gridWidth = GAME_WIDTH - 100 - 250; // 610px

    // セクション区切り線
    this.add.rectangle(gridLeft + gridWidth / 2, sectionY, gridWidth, 1, 0x555577);

    this.add.text(gridLeft, sectionY + 12, 'カスタムAIキャラ', {
      fontSize: '14px',
      color: '#aaaacc',
      fontFamily: "'Noto Sans JP', Arial, sans-serif",
    });

    const customAIs = loadCustomAICharacters();

    if (customAIs.length === 0) {
      this.add.text(gridLeft + gridWidth / 2, sectionY + 40, 'AIキャラがありません（タイトル→AIキャラ作成で追加）', {
        fontSize: '13px',
        color: '#666688',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      }).setOrigin(0.5);
      return;
    }

    const cardWidth = gridWidth / 2 - 8;
    const cardHeight = 36;
    const colGap = gridWidth / 2;

    customAIs.forEach((char, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const cx = gridLeft + col * colGap + cardWidth / 2;
      const cy = sectionY + 40 + row * 44 + cardHeight / 2;

      const bg = this.add
        .rectangle(cx, cy, cardWidth, cardHeight, 0x223344)
        .setStrokeStyle(1, 0x446688)
        .setInteractive({ useHandCursor: true });

      this.customAICardBgs.set(char.id, bg);

      this.add.text(cx, cy - 7, char.name, {
        fontSize: '14px',
        color: '#88ccff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      }).setOrigin(0.5);

      const monsterName = this.getMonsterName(char.monsterId);
      this.add.text(cx, cy + 9, `ベース: ${monsterName}`, {
        fontSize: '11px',
        color: '#8888aa',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      }).setOrigin(0.5);

      bg.on('pointerover', () => {
        if (this.selectedCustomAIConfig !== char.aiConfig) {
          bg.setFillStyle(0x334455);
        }
      });
      bg.on('pointerout', () => {
        if (this.selectedCustomAIConfig !== char.aiConfig) {
          bg.setFillStyle(0x223344);
        }
      });
      bg.on('pointerdown', () => this.selectCustomAI(char));
    });
  }

  private selectCustomAI(char: CustomAICharacter): void {
    this.selectedMonsterId = char.monsterId;
    this.selectedCustomAIConfig = char.aiConfig;
    this.updateParameterDisplay(char.monsterId);
    this.updateGridSelection(null);
    this.updateCustomAICardSelection(char.id);
  }

  private updateCustomAICardSelection(selectedId: string | null): void {
    this.customAICardBgs.forEach((bg, id) => {
      bg.setFillStyle(id === selectedId ? 0x1a4466 : 0x223344);
      bg.setStrokeStyle(1, id === selectedId ? 0x88ccff : 0x446688);
    });
  }

  private getMonsterName(monsterId: string): string {
    return MONSTER_DATABASE.find((m) => m.id === monsterId)?.name ?? monsterId;
  }

  private selectMonster(monsterId: string): void {
    if (monsterId !== this.selectedMonsterId) {
      this.selectedCustomAIConfig = undefined;
      this.updateCustomAICardSelection(null);
    }
    this.selectedMonsterId = monsterId;
    this.updateParameterDisplay(monsterId);
    this.updateGridSelection(monsterId);
  }

  private getDisplayMonster(monsterId: string): Monster | undefined {
    if (this.mode === GameMode.CHALLENGE) {
      return getMonsterWithGrownStats(monsterId, Math.min(this.clearedStages ?? 0, MAX_GROWTH_STAGES));
    }
    if (this.mode === GameMode.FREE_CPU || this.mode === GameMode.FREE_CPU_3V3 || this.mode === GameMode.SPECTATOR) {
      return getMonsterWithFinalStats(monsterId);
    }
    return MONSTER_DATABASE.find((m) => m.id === monsterId);
  }

  private updateParameterDisplay(monsterId: string): void {
    // 既存のパラメータテキストを削除
    this.parameterTexts.forEach((t) => t.destroy());
    this.parameterTexts = [];

    const monster = this.getDisplayMonster(monsterId);
    if (!monster) return;

    const panelX = GAME_WIDTH - 220;
    const panelY = 105;
    const lineHeight = 27;

    const lines = [
      { label: `名前：${monster.name}`, color: THEME_COLORS[monster.id] || '#ffffff' },
      { label: `HP: ${monster.stats.hp}`, color: '#88ff88' },
      { label: `腕力: ${monster.stats.strength}`, color: '#ff8888' },
      { label: `特殊: ${monster.stats.special}`, color: '#8888ff' },
      { label: `素早さ: ${monster.stats.speed}`, color: '#ffff88' },
      { label: `丈夫さ: ${monster.stats.toughness}`, color: '#88ffff' },
      { label: `特殊回数: ${monster.stats.specialAttackCount}`, color: '#ff88ff' },
      { label: `武器: ${monster.weapon.name}(×${monster.weapon.multiplier})`, color: '#ffffff' },
      { label: `反射: ${monster.reflector.name}(${monster.reflector.maxReflectCount}回/×${monster.reflector.reflectRate})`, color: '#ffffff' },
    ];

    if (this.mode === GameMode.SURVIVAL) {
      const best = loadSurvivalBest(monsterId);
      lines.push({ label: `最高連勝: ${best}`, color: '#ffd700' });
    }

    lines.forEach((line, i) => {
      const text = this.add
        .text(panelX + 10, panelY + i * lineHeight, line.label, {
          fontSize: '16px',
          color: line.color,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        });
      this.parameterTexts.push(text);
    });
  }

  /** selected/hovered 状態から正しい色を計算してセルに適用する */
  private applyCellColor(monsterId: string): void {
    const bg = this.cellBgs.get(monsterId);
    if (!bg) return;
    const isAlreadyPicked = this.isBossStage && this.playerParty.includes(monsterId);
    if (isAlreadyPicked) {
      bg.setFillStyle(0x222222);
      bg.setStrokeStyle(2, 0x444444);
      return;
    }
    const isSelected = this.selectedMonsterId === monsterId;
    const isHovered = this.hoveredMonsterId === monsterId;
    bg.setFillStyle(isSelected ? 0x555577 : isHovered ? 0x444466 : 0x333355);
    bg.setStrokeStyle(2, isSelected ? 0xaaaaff : 0x666699);
  }

  private updateGridSelection(_monsterId: string | null): void {
    for (const id of this.cellBgs.keys()) {
      this.applyCellColor(id);
    }
  }

  private createDetailButton(): void {
    const panelX = GAME_WIDTH - 220;
    const buttonY = 390;

    const btn = this.add
      .text(panelX + 100, buttonY, '詳細', {
        fontSize: '14px',
        color: '#aaddff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    btn.on('pointerover', () => btn.setColor('#ffffff'));
    btn.on('pointerout', () => btn.setColor('#aaddff'));
    btn.on('pointerdown', () => {
      if (this.selectedMonsterId) {
        this.showDetailPanel(this.selectedMonsterId);
      }
    });
  }

  private createBgmSelector(): void {
    const BGM_OPTIONS: { label: string; key: AudioKeyValue | 'bgm_random' }[] = [
      { label: 'ランダム',     key: 'bgm_random' },
      { label: '対戦1',        key: AudioKey.BGM_BATTLE },
      { label: '大会',         key: AudioKey.BGM_TOURNAMENT },
      { label: 'サバイバル1',  key: AudioKey.BGM_SURVIVAL_1 },
      { label: 'サバイバル2',  key: AudioKey.BGM_SURVIVAL_2 },
      { label: 'サバイバル3',  key: AudioKey.BGM_SURVIVAL_3 },
    ];

    const dropdownW = 110;
    const dropdownH = 24;
    const anchorX = GAME_WIDTH - 16; // 右端から16px
    const anchorY = GAME_HEIGHT - 16; // 下端から16px

    // ラベル「BGM:」
    this.add
      .text(anchorX - dropdownW - 4, anchorY, 'BGM:', {
        fontSize: '13px',
        color: '#aaaaaa',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(1, 0.5)
      .setDepth(10);

    // ドロップダウン本体（選択中ラベル表示）
    const getLabel = () =>
      BGM_OPTIONS.find((o) => o.key === this.selectedBgmKey)?.label ?? 'デフォルト';

    const headerBg = this.add
      .rectangle(anchorX, anchorY, dropdownW, dropdownH, 0x333333)
      .setOrigin(1, 0.5)
      .setDepth(10)
      .setInteractive({ useHandCursor: true });

    const headerText = this.add
      .text(anchorX - dropdownW / 2, anchorY, `${getLabel()} ▼`, {
        fontSize: '13px',
        color: '#ffdd44',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(11);

    // ドロップダウンリスト（初期非表示）
    const itemH = 24;
    const listObjects: Phaser.GameObjects.GameObject[] = [];
    let isOpen = false;

    const closeList = () => {
      listObjects.forEach((o) => o.destroy());
      listObjects.length = 0;
      isOpen = false;
      headerText.setText(`${getLabel()} ▼`);
    };

    const openList = () => {
      isOpen = true;
      headerText.setText(`${getLabel()} ▲`);

      BGM_OPTIONS.forEach((option, i) => {
        const itemY = anchorY - dropdownH / 2 - (BGM_OPTIONS.length - i) * itemH;
        const isSelected = this.selectedBgmKey === option.key;

        const bg = this.add
          .rectangle(anchorX, itemY, dropdownW, itemH, isSelected ? 0x444400 : 0x222222)
          .setOrigin(1, 0.5)
          .setDepth(20)
          .setInteractive({ useHandCursor: true });

        const label = this.add
          .text(anchorX - dropdownW / 2, itemY, option.label, {
            fontSize: '13px',
            color: isSelected ? '#ffdd44' : '#cccccc',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
          })
          .setOrigin(0.5, 0.5)
          .setDepth(21);

        bg.on('pointerover', () => { if (!isSelected) bg.setFillStyle(0x444444); });
        bg.on('pointerout', () => { if (!isSelected) bg.setFillStyle(0x222222); });
        bg.on('pointerdown', () => {
          this.selectedBgmKey = option.key as AudioKeyValue | 'bgm_random';
          closeList();
        });

        listObjects.push(bg, label);
      });
    };

    headerBg.on('pointerdown', () => {
      if (isOpen) closeList();
      else openList();
    });
  }

  private createBattleBgSelector(): void {
    const BG_OPTIONS: { label: string; key: string }[] = [
      { label: 'ランダム',     key: 'bg_random' },
      { label: '幽炎の闘技場', key: 'bg_battle' },
      { label: '朱暮の廃址',   key: 'bg_battle_2' },
      { label: '業火の祭壇',   key: 'bg_battle_3' },
      { label: '霞峰の遺址',   key: 'bg_battle_4' },
      { label: '氷晶の洞窟',   key: 'bg_battle_5' },
      { label: '月砂の荒野',   key: 'bg_battle_6' },
      { label: '嵐渦の天砦',   key: 'bg_battle_7' },
      { label: '極光の聖域',   key: 'bg_battle_luna' },
      { label: '皇都の闘技場', key: 'bg_battle_arena' },
    ];

    const dropdownW = 160;
    const dropdownH = 24;
    const anchorX = GAME_WIDTH - 16;
    const anchorY = GAME_HEIGHT - 44; // BGMセレクターの上

    this.add
      .text(anchorX - dropdownW - 4, anchorY, 'MAP:', {
        fontSize: '13px',
        color: '#aaaaaa',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(1, 0.5)
      .setDepth(10);

    const getLabel = () =>
      BG_OPTIONS.find((o) => o.key === this.selectedBattleBg)?.label ?? 'デフォルト';

    const headerBg = this.add
      .rectangle(anchorX, anchorY, dropdownW, dropdownH, 0x333333)
      .setOrigin(1, 0.5)
      .setDepth(10)
      .setInteractive({ useHandCursor: true });

    const headerText = this.add
      .text(anchorX - dropdownW / 2, anchorY, `${getLabel()} ▼`, {
        fontSize: '13px',
        color: '#44ddff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(11);

    const listObjects: Phaser.GameObjects.GameObject[] = [];
    let isOpen = false;

    const closeList = () => {
      listObjects.forEach((o) => o.destroy());
      listObjects.length = 0;
      isOpen = false;
      headerText.setText(`${getLabel()} ▼`);
    };

    const openList = () => {
      isOpen = true;
      headerText.setText(`${getLabel()} ▲`);

      BG_OPTIONS.forEach((option, i) => {
        const itemY = anchorY - dropdownH / 2 - (BG_OPTIONS.length - i) * dropdownH;
        const isSelected = this.selectedBattleBg === option.key;

        const bg = this.add
          .rectangle(anchorX, itemY, dropdownW, dropdownH, isSelected ? 0x004444 : 0x222222)
          .setOrigin(1, 0.5)
          .setDepth(20)
          .setInteractive({ useHandCursor: true });

        const label = this.add
          .text(anchorX - dropdownW / 2, itemY, option.label, {
            fontSize: '13px',
            color: isSelected ? '#44ddff' : '#cccccc',
            fontFamily: "'Noto Sans JP', Arial, sans-serif",
          })
          .setOrigin(0.5, 0.5)
          .setDepth(21);

        bg.on('pointerover', () => { if (!isSelected) bg.setFillStyle(0x444444); });
        bg.on('pointerout', () => { if (!isSelected) bg.setFillStyle(0x222222); });
        bg.on('pointerdown', () => {
          this.selectedBattleBg = option.key;
          closeList();
        });

        listObjects.push(bg, label);
      });
    };

    headerBg.on('pointerdown', () => {
      if (isOpen) closeList();
      else openList();
    });
  }

  private showDetailPanel(monsterId: string): void {
    this.closeDetailPanel();

    const monster = this.getDisplayMonster(monsterId);
    const detail = getMonsterDetailData(monsterId);
    if (!monster || !detail) return;

    const cfg = DETAIL_PANEL_CONFIG;

    // 半透明オーバーレイ（クリックで閉じる）
    const overlay = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setDepth(cfg.overlayDepth)
      .setInteractive()
      .on('pointerdown', () => this.closeDetailPanel());
    this.detailPanelObjects.push(overlay);

    // パネル背景
    const panel = this.add
      .rectangle(cfg.panelCenterX, cfg.panelCenterY, cfg.panelWidth, cfg.panelHeight, 0x1a1a3a)
      .setDepth(cfg.overlayDepth + 1)
      .setStrokeStyle(2, 0x8888cc);
    this.detailPanelObjects.push(panel);

    const themeColor = THEME_COLORS[monster.id] || '#ffffff';
    const panelLeft = cfg.panelCenterX - cfg.panelWidth / 2;
    const imageAreaCenterX = panelLeft + 130;
    const textAreaLeft = panelLeft + 270;
    const left = textAreaLeft;

    const contentDepth = cfg.overlayDepth + 2;

    // キャラ画像（左エリア）- 静止画
    const battleKey = `monster_${monsterId}_battle`;
    const imageY = cfg.panelCenterY + 10;

    if (this.textures.exists(battleKey)) {
      const charImage = this.add.image(imageAreaCenterX, imageY, battleKey)
        .setDisplaySize(200, 200)
        .setDepth(contentDepth);
      this.detailPanelObjects.push(charImage);
    }

    // モンスター名・魂格
    const nameText = this.add
      .text(cfg.panelCenterX, cfg.panelCenterY - cfg.panelHeight / 2 + 22, `名前: ${monster.name}`, {
        fontSize: '22px',
        color: themeColor,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(contentDepth);
    this.detailPanelObjects.push(nameText);

    const speciesText = this.add
      .text(cfg.panelCenterX, cfg.panelCenterY - cfg.panelHeight / 2 + 48, `魂格: ${monster.species}`, {
        fontSize: '16px',
        color: '#cccccc',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
      })
      .setOrigin(0.5)
      .setDepth(contentDepth);
    this.detailPanelObjects.push(speciesText);

    // 説明文
    const descText = this.add
      .text(left, cfg.panelCenterY - cfg.panelHeight / 2 + 80, detail.speciesDescription, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        wordWrap: { width: cfg.panelWidth - 290, useAdvancedWrap: true },
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setDepth(contentDepth);
    this.detailPanelObjects.push(descText);

    // 得意距離・推奨スタンス・戦法ヒント
    const infoLines = [
      { label: `得意距離: ${detail.preferredRange}`, color: '#88ffff' },
      { label: `推奨スタンス: ${detail.recommendedStance}`, color: '#ffff88' },
      { label: `戦法: ${detail.battleHint}`, color: '#ffcc88' },
    ];

    infoLines.forEach((line, i) => {
      const infoText = this.add
        .text(left, cfg.panelCenterY + 30 + i * cfg.lineHeight, line.label, {
          fontSize: '15px',
          color: line.color,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          wordWrap: { width: cfg.panelWidth - 290, useAdvancedWrap: true },
        })
        .setDepth(contentDepth);
      this.detailPanelObjects.push(infoText);
    });

    // 閉じるボタン
    const closeBtn = this.add
      .text(cfg.panelCenterX + cfg.panelWidth / 2 - 20, cfg.panelCenterY - cfg.panelHeight / 2 + 15, '×', {
        fontSize: '20px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(contentDepth)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.closeDetailPanel());
    this.detailPanelObjects.push(closeBtn);
  }

  private closeDetailPanel(): void {
    this.detailPanelObjects.forEach(obj => (obj as Phaser.GameObjects.GameObject).destroy());
    this.detailPanelObjects = [];
  }

  private startSurvivalMode(): void {
    if (!this.selectedMonsterId) return;
    // 既存のセーブデータを削除して新規開始
    deleteSurvivalSave();
    const monsterId = this.selectedMonsterId;
    const baseMonster = MONSTER_DATABASE.find((m) => m.id === monsterId);
    if (!baseMonster) return;

    const opponentQueue = createShuffledOpponentQueue(monsterId);
    const enemyId = opponentQueue[0];
    const enemyStage = getSurvivalEnemyStage(0);
    const aiLevel = getSurvivalAiLevel(0);
    const enemy = getMonsterWithGrownStats(enemyId, enemyStage);
    if (!enemy) return;

    const playerMonster: Monster = {
      ...baseMonster,
      stats: { ...baseMonster.stats },
    };

    const saveData: SurvivalSaveData = {
      monsterId,
      winCount: 0,
      bonusPoints: 0,
      stats: { ...baseMonster.stats },
      opponentQueue,
    };

    this.transitionTo(SceneKey.BATTLE, {
      mode: GameMode.SURVIVAL,
      survivalSaveData: saveData,
      playerMonster,
      enemyMonster: enemy,
      aiLevel,
    });
  }

  private showWaitingForOpponent(): void {
    this.waitingText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '相手のキャラ選択を待っています...', {
        fontSize: '20px',
        color: '#aaaaff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        backgroundColor: '#000000cc',
        padding: { x: 20, y: 10 },
      })
      .setOrigin(0.5)
      .setDepth(100) as unknown as Phaser.GameObjects.Text;
  }
}
